import { useEffect, useMemo, useState } from 'react';
import { SelectedFieldsType } from '@fabric-ui/components';
import { CALCULATED_DIMENSIONS_GROUP } from '@views/CalculateDimensions/utils';
import { useCustomSearchParams } from '../hooks/UseCustomSearchParams2';
import { trackEvent } from '../services/analytics.service';
import { isNullOrUndefined } from '../utils/utils';
import { FieldType } from '../views/CustomDashboards/types/types';

export const getVisibleDims = ({ dims, dimsToRemove = new Set(), isUnpopulatedFieldsActive = true, ignoreVisibilityConfig = false }) =>
  dims.filter(
    dim =>
      (ignoreVisibilityConfig || !dim.visibilityConfig?.config?.hidden) &&
      !dimsToRemove.has(dim.value) &&
      (isUnpopulatedFieldsActive ? true : dim.isPopulated)
  );

interface UseFilterFieldsProps {
  dims: any[];
  metrics: any[];
  calcDims?: any[];
  selectedFields?: SelectedFieldsType;
  onChangeFilterType?: (event: React.MouseEvent<HTMLElement> | null, fieldsTypes: string) => any;
  topFieldsEnabled: boolean;
  populatedFieldsEnabled: boolean;
  ignoreVisibilityConfig?: boolean;
  isHistoricalActive?: boolean;
  activeFilterType?: FieldType | null;
}

export default function useFilterFields({
  dims,
  metrics,
  calcDims = [],
  selectedFields,
  onChangeFilterType,
  topFieldsEnabled,
  populatedFieldsEnabled,
  ignoreVisibilityConfig,
  activeFilterType: activeFilterTypeProp,
}: UseFilterFieldsProps) {
  const [activeFilterType, setActiveFilterType] = useState<FieldType | null>(activeFilterTypeProp ?? FieldType.All);
  const [isTopFieldsFilterActive, setIsTopFieldsFilterActive] = useState(topFieldsEnabled);
  const [isInUseFilterActive, setIsInUseFilterActive] = useState(false);
  const [isUnpopulatedFieldsActive, setIsUnpopulatedFieldsActive] = useState(!populatedFieldsEnabled);

  const [filters] = useCustomSearchParams();

  useEffect(() => {
    if (!isNullOrUndefined(activeFilterTypeProp) && activeFilterTypeProp !== activeFilterType) {
      setActiveFilterType(activeFilterTypeProp);
    } else if (isNullOrUndefined(activeFilterTypeProp) && filters.dataSelectTypes && filters.dataSelectTypes !== activeFilterType) {
      setActiveFilterType(filters.dataSelectTypes);
    }
  }, [filters, activeFilterTypeProp]);

  const filterByTopFields = () => {
    trackEvent(isTopFieldsFilterActive ? 'Top Fields - Deactivate' : 'Top Fields - Activate');
    setIsTopFieldsFilterActive(prevState => !prevState);
  };

  const filterByUsedFields = () => setIsInUseFilterActive(prevState => !prevState);

  const filterByUnpopulatedFields = () => {
    trackEvent(isUnpopulatedFieldsActive ? 'Include Empty Fields - Deactivate' : 'Include Empty Fields - Activate');
    setIsUnpopulatedFieldsActive(prevState => !prevState);
  };

  const getUsedFields = fields => fields.filter(field => !!selectedFields?.[field.value ?? field.systemName]);

  const getFieldsByFilterType = () => {
    if (activeFilterType === FieldType.Measurements) {
      return metrics;
    }

    const filteredDims = getVisibleDims({ dims, isUnpopulatedFieldsActive, ignoreVisibilityConfig });
    if (activeFilterType === FieldType.Dimensions) {
      return [...filteredDims, ...calcDims];
    }

    return [...filteredDims, ...calcDims, ...metrics];
  };

  const fields = useMemo(() => {
    const baseFields = getFieldsByFilterType();
    // Calculated dimensions are never flagged isRecommended, so exempt them from the Top Fields cut.
    // They are still subject to the In Use filter like any other field.
    const topFields = isTopFieldsFilterActive
      ? baseFields.filter(({ group, isRecommended }) => group === CALCULATED_DIMENSIONS_GROUP || isRecommended)
      : baseFields;

    return isInUseFilterActive ? getUsedFields(topFields) : topFields;
  }, [dims, metrics, calcDims, activeFilterType, isTopFieldsFilterActive, isInUseFilterActive, isUnpopulatedFieldsActive]);

  const initFilters = () => {
    setIsTopFieldsFilterActive(true);
    setIsInUseFilterActive(false);
    setIsUnpopulatedFieldsActive(!populatedFieldsEnabled);
    setActiveFilterType(FieldType.All);
  };

  const resetFilterType = () => setActiveFilterType(FieldType.All);

  const resetFilters = () => {
    trackEvent('Clear Filters');

    setIsTopFieldsFilterActive(false);
    setIsInUseFilterActive(false);

    setIsUnpopulatedFieldsActive(true);

    setActiveFilterType(FieldType.All);
  };

  const onClickFilterType = (filterType: FieldType) => {
    if (filterType === activeFilterType) {
      setActiveFilterType(null);
      onChangeFilterType?.(null, FieldType.All);
    } else {
      setActiveFilterType(filterType);
      onChangeFilterType?.(null, filterType);
    }
  };

  return {
    fields,
    activeFilterType,
    topFields: {
      isTopFieldsFilterActive,
      filterByTopFields,
    },
    inUse: {
      isInUseFilterActive,
      filterByUsedFields,
    },
    unpopulated: {
      isUnpopulatedFieldsActive,
      filterByUnpopulatedFields,
    },
    onClickFilterType,
    initFilters,
    resetFilters,
    resetFilterType,
  };
}
